import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import Page from '../components/Page';
import Crumbs from '../components/Crumbs';
import Pill from '../components/Pill';
import { fmt, weiToEth, short } from '../lib/format';
import { useReadContracts } from '../hooks/useContracts';
import { useRequestCount } from '../hooks/useAuditingPoolConfig';
import { type RequestRow } from '../hooks/useRequests';

type Status = 'all' | 'voting' | 'approved' | 'disputed' | 'final' | 'cancelled';

const FILTERS: { key: Status; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'voting', label: 'Voting' },
  { key: 'approved', label: 'Approved' },
  { key: 'disputed', label: 'Disputed' },
  { key: 'final', label: 'Final' },
  { key: 'cancelled', label: 'Cancelled' },
];

/**
 * Compute the high-level status from on-chain flags.
 * `disputed` and `final` aren't in the contract — we infer:
 *   final     = verdictAccepted && currentMilestone has reached the last
 *               approved milestone AND the latest milestone is Approved.
 *   disputed  = the contract has a Disputed milestone state — covered
 *               by getMilestoneStatus per-milestone, not on the request
 *               row alone. We approximate "disputed" as "approved verdict
 *               but at least one milestone in dispute" via separate
 *               check (omitted at list level for performance).
 *   voting    = !verdictCast (auditors still casting)
 *   approved  = verdictCast && verdictAccepted && !cancelled && !final
 *   cancelled = cancelled
 */
function statusOf(r: RequestRow | undefined): Status {
  if (!r) return 'voting';
  if (r.cancelled) return 'cancelled';
  if (!r.verdictCast) return 'voting';
  if (!r.verdictAccepted) return 'cancelled';
  return 'approved';
  // 'final' / 'disputed' need per-milestone reads — refined on the request page itself.
}

const TONE: Record<Status, { tone: 'green' | 'amber' | 'red' | 'blue' | 'gray'; label: string }> = {
  all:        { tone: 'gray',  label: 'All'        },
  voting:     { tone: 'blue',  label: 'Voting'     },
  approved:   { tone: 'green', label: 'Approved'   },
  disputed:   { tone: 'amber', label: 'Disputed'   },
  final:      { tone: 'blue',  label: 'Final'      },
  cancelled:  { tone: 'red',   label: 'Cancelled'  },
};

export default function Auditing() {
  const [filter, setFilter] = useState<Status>('all');
  const [search, setSearch] = useState('');
  const contracts = useReadContracts();
  const { data: count, isLoading: countLoading } = useRequestCount();
  const N = count !== undefined ? Number(count) : 0;

  // Walk requestList(0..N-1) in parallel via TanStack useQueries
  const queries = useQueries({
    queries: Array.from({ length: N }, (_, i) => ({
      queryKey: ['metha', 'auditingPool', 'requestList', i] as const,
      queryFn: async () => {
        const row = (await contracts.auditingPool.requestList(BigInt(i))) as unknown as RequestRow;
        return row;
      },
      staleTime: 20_000,
    })),
  });

  // Compute aggregates once over the live list
  const rows = useMemo(() => {
    return queries
      .map((q, i) => (q.data ? { id: i, row: q.data } : null))
      .filter((x): x is { id: number; row: RequestRow } => x !== null);
  }, [queries]);

  const counts = useMemo(() => {
    const c = { open: 0, approved: 0, voting: 0, cancelled: 0, total: rows.length, disbursedWei: 0n };
    for (const { row } of rows) {
      const s = statusOf(row);
      if (s === 'approved') c.approved++;
      if (s === 'voting')   c.voting++;
      if (s === 'cancelled') c.cancelled++;
      if (s !== 'cancelled' && row.verdictAccepted) c.open++;
    }
    return c;
  }, [rows]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter(({ row }) => {
      if (filter !== 'all' && statusOf(row) !== filter) return false;
      if (!term) return true;
      return (
        row.title.toLowerCase().includes(term) ||
        row.labWallet.toLowerCase().includes(term) ||
        row.ipfsHash.toLowerCase().includes(term)
      );
    });
  }, [rows, filter, search]);

  return (
    <Page ctxLabel="auditing" title="Metha · Auditing" docsPath="guides/auditor" docsLabel="Auditor guide">
      <Crumbs crumbs={[{ to: '/', label: 'Overview' }, { label: 'Auditing' }]} />

      <div className="ph">
        <div>
          <h1>Auditing <span className="acc">/ {countLoading ? '…' : `${N} requests on-chain`}</span></h1>
          <p className="lede">
            The quality gateway. Every request runs through the six-state milestone
            machine: <span className="mono" style={{ fontSize: 11 }}>
            Locked → Unlocked → Claimed → ProofSubmitted → Disputed → Approved</span>.
            Funded research only releases capital milestone-by-milestone.
          </p>
        </div>
        <div className="meta">
          <span>OPEN <b>{counts.open}</b></span>
          <span>APPROVED <b style={{ color: 'var(--green)' }}>{counts.approved}</b></span>
          <span>CANCELLED <b style={{ color: 'var(--red)' }}>{counts.cancelled}</b></span>
        </div>
      </div>

      <div className="kpi-row mb-24" style={{ gridTemplateColumns: 'repeat(5,1fr)' }}>
        <Kpi l="REQUESTS · TOTAL" v={String(counts.total)} d="across all periods" />
        <Kpi l="APPROVED" v={String(counts.approved)}
             d={counts.total > 0 ? `${Math.round((counts.approved / counts.total) * 100)}% acceptance` : '—'} tone="up" />
        <Kpi l="VOTING" v={String(counts.voting)} d="awaiting verdict" />
        <Kpi l="CANCELLED" v={String(counts.cancelled)} d="deposit burned" />
        <Kpi l="LIVE FETCH" v={queries.filter((q) => q.isLoading).length === 0 ? '✓' : '…'} d={`${rows.length} / ${N} loaded`} />
      </div>

      <div className="filters mb-14">
        <span className="lab">Filter</span>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            className={`chip${filter === f.key ? ' active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
        <span className="search">
          <input
            type="text"
            placeholder="Search by title, lab, IPFS hash…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </span>
        <Link to="/request/new" className="btn btn-prim">+ New request</Link>
      </div>

      <div className="panel">
        <div className="p-head">
          <h3>All research requests</h3>
          <span className="src"><code>auditingPool.requestList × {N}</code></span>
        </div>
        <div className="tbl">
          <div className="th" style={{ gridTemplateColumns: '60px 1fr 100px 90px 110px' }}>
            <span>ID</span><span>RESEARCHER</span><span>HYPE</span><span>VOTES</span><span>STATUS</span>
          </div>
          {N === 0 && !countLoading && (
            <div style={{ padding: 24, color: 'var(--dim)', fontFamily: 'var(--mono)', fontSize: 12 }}>
              No research requests submitted yet — be the first lab.
              <br />
              <Link to="/request/new" className="acc">Submit a research request →</Link>
            </div>
          )}
          {visible.map(({ id, row }) => {
            const s = statusOf(row);
            const tone = TONE[s];
            return (
              <Link key={id} to={`/request/${id}`} className="tr"
                    style={{ gridTemplateColumns: '60px 1fr 100px 90px 110px', textDecoration: 'none' }}>
                <span className="pid">#{String(id).padStart(3, '0')}</span>
                <span className="who">
                  {row.title || <span className="dim">(no title)</span>}
                  <small>
                    {short(row.labWallet)}{' '}
                    {row.ipfsHash && <>· IPFS {row.ipfsHash.slice(0, 8)}…</>}
                  </small>
                </span>
                <span>↑ {fmt.meth(weiToEth(row.hype))}</span>
                <span>{Number(row.approveVotes)} ✓ / {Number(row.totalVotes)}</span>
                <span><Pill tone={tone.tone}>{tone.label}</Pill></span>
              </Link>
            );
          })}
        </div>
      </div>

      <div className="ph mt-32">
        <div>
          <h1>Auditor inbox <span className="acc">/ assigned audits</span></h1>
          <p className="lede">
            Audits assigned to active auditors. Click a request to cast vote / review proof.
            Want to become an auditor? <Link to="/auditors" className="acc">Apply →</Link>
          </p>
        </div>
      </div>

      <div className="panel">
        <div className="p-head"><h3>Pending audits</h3>
          <span className="src">isAuditorFor(reqId, addr) per-row · per-auditor walk</span></div>
        <div className="p-body" style={{ padding: 18, color: 'var(--dim)', fontFamily: 'var(--mono)', fontSize: 11 }}>
          Per-auditor inbox derives from a triple-loop (auditors × requests × milestones).
          Will populate when an active auditor connects · for now use a request page directly.
        </div>
      </div>

    </Page>
  );
}

function Kpi({ l, v, d, tone }: { l: string; v: ReactNode; d: ReactNode; tone?: 'up' | 'dn' }) {
  return (
    <div className="kpi">
      <span className="l">{l}</span>
      <span className="v">{v}</span>
      <span className={`d${tone ? ' ' + tone : ''}`}>{d}</span>
    </div>
  );
}
